export default {
  translation: {
    // Common
    "app.name": "Give Protocol",
    "app.tagline": "Transparent, borderless infrastructure for generosity.",
    "common.skipToMainContent": "Skip to main content",
    "common.cancel": "Cancel",
    "common.edit": "Edit",
    "common.delete": "Delete",
    "common.creating": "Creating...",
    "common.unknown": "Unknown",
    "common.notAvailable": "N/A",

    // Navigation
    "nav.browse": "Browse Charities",
    "nav.opportunities": "Volunteer Opportunities",
    "nav.contributions": "Contribution Tracker",
    "nav.governance": "Governance",
    "nav.dashboard": "Give Dashboard",
    "nav.about": "About",
    "nav.docs": "Documentation",
    "nav.legal": "Legal",
    "nav.launchApp": "Launch App",

    // FAQ page (GIV-988)
    "faq.title": "Frequently Asked Questions",
    "faq.subtitle":
      "Everything you need to know about giving, volunteering, and blockchain transparency.",

    // Settings
    "settings.title": "Settings",
    "settings.language": "Language",
    "settings.currency": "Currency",

    // Auth
    "auth.login": "Sign In",
    "auth.register": "Create Account",
    "auth.logout": "Sign Out",
    "auth.forgotPassword": "Forgot Password?",

    // Dashboard
    "dashboard.title": "Give Dashboard",
    "dashboard.subtitle": "Track your donations and volunteer contributions",
    "dashboard.totalDonations": "Total Donations",
    "dashboard.volunteerHours": "Volunteer Hours",
    "dashboard.skillsEndorsed": "Skills Endorsed",
    "dashboard.contributions": "Contributions",
    "dashboard.yourTotalDonated": "Your Total Donated",
    "dashboard.yourVolunteerHours": "Your Volunteer Hours",
    "dashboard.yourSkillsEndorsed": "Your Skills Endorsed",
    "dashboard.impactGrowth": "Impact Growth",
    "dashboard.charitiesSupported": "Charities Supported",

    // Charity
    "charity.dashboard": "Charity Dashboard",
    "charity.subtitle": "Manage your donations and volunteer programs",
    "charity.activeVolunteers": "Active Volunteers",
    "charity.transactions": "Transactions",
    "charity.volunteers": "Volunteer Hours",
    "charity.applications": "Applications",
    "charity.totalReceived": "Total Received",
    "charity.equityPoolValue": "Equity Pool Value",
    "charity.availableBalance": "Available Balance",

    // Contributions
    "contributions.date": "Date",
    "contributions.type": "Type",
    "contributions.organization": "Organization",
    "contributions.details": "Details",
    "contributions.status": "Status",
    "contributions.verification": "Verification",
    "contributions.export": "Export CSV",

    // Volunteer
    "volunteer.opportunities": "Volunteer Opportunities",
    "volunteer.searchOpportunities": "Search opportunities...",
    "volunteer.selectSkill": "Select skill",
    "volunteer.selectType": "Select type",
    "volunteer.selectLanguage": "Select language",
    "volunteer.allSkills": "All Skills",
    "volunteer.allTypes": "All Types",
    "volunteer.allLanguages": "All Languages",
    "volunteer.applyNow": "Apply Now",
    "volunteer.noOpportunitiesFound":
      "No opportunities found matching your criteria.",
    "volunteer.verify": "Verify Hours",
    "volunteer.verifying": "Verifying...",
    "volunteer.reject": "Reject",
    "volunteer.accept": "Accept",
    "volunteer.processing": "Processing...",
    "volunteer.hours": "hours on",
    "volunteer.description": "Description",
    "volunteer.appliedFor": "Applied for",
    "volunteer.verificationComplete": "Verification Complete",
    "volunteer.hoursVerified":
      "The volunteer hours have been verified and recorded on the blockchain.",
    "volunteer.verificationHash": "Verification Hash",
    "volunteer.applicationAccepted": "Application Accepted",
    "volunteer.applicationRecorded":
      "The volunteer application has been accepted and recorded on the blockchain.",
    "volunteer.acceptanceHash": "Acceptance Hash",
    "volunteer.pendingHours": "Pending Volunteer Hours",
    "volunteer.noPendingHours": "No pending volunteer hours to verify.",
    "volunteer.pendingApplications": "Pending Applications",
    "volunteer.noPendingApplications": "No pending applications to review.",
    "volunteer.createOpportunity": "Create Volunteer Opportunity",
    "volunteer.createNew": "Create New",
    "volunteer.noOpportunitiesYet":
      'No opportunities created yet. Click "Create New" to get started.',
    "volunteer.opportunityTitle": "Opportunity Title",
    "volunteer.skills": "Skills (comma-separated)",
    "volunteer.commitment": "Commitment",
    "volunteer.location": "Location",
    "volunteer.type": "Type",
    "volunteer.workLanguage": "Work Language",
    "volunteer.status": "Status",

    // Volunteer commitment types
    "volunteer.commitment.oneTime": "One-time",
    "volunteer.commitment.shortTerm": "Short-term",
    "volunteer.commitment.longTerm": "Long-term",

    // Volunteer types
    "volunteer.type.remote": "Remote",
    "volunteer.type.onsite": "Onsite",
    "volunteer.type.hybrid": "Hybrid",
    "volunteer.type.onSite": "On-site",

    // Volunteer - Opportunities page
    "volunteer.workTypeFilter": "Work type filter",
    "volunteer.searchLocation": "City or region...",
    "volunteer.searchLocationAria": "Search location",
    "volunteer.filter.skill": "Skill: {{value}}",
    "volunteer.filter.type": "Type: {{value}}",
    "volunteer.filter.language": "Language: {{value}}",
    "volunteer.filter.location": "Location: {{value}}",
    "volunteer.loadingOpportunities": "Loading opportunities...",
    "volunteer.loadOpportunitiesError": "We couldn't load volunteer opportunities. Please try again later.",
    "volunteer.viewDetails": "View details",
    "volunteer.backToOpportunities": "Back to opportunities",
    "volunteer.detail.about": "About this opportunity",
    "volunteer.detail.skills": "Skills needed",
    "volunteer.detail.commitment": "Time commitment",
    "volunteer.detail.location": "Location",
    "volunteer.detail.language": "Language",
    "volunteer.detail.hostedBy": "Hosted by",
    "volunteer.detail.ongoing": "Ongoing",
    "volunteer.detail.applicationsClosed": "Applications closed",
    "volunteer.detail.notFoundTitle": "Opportunity not found",
    "volunteer.detail.notFoundMessage": "This opportunity may have been filled or removed.",
    "volunteer.field.requirements": "Requirements",
    "volunteer.field.benefits": "Benefits",
    "volunteer.field.schedule": "Schedule",
    "volunteer.field.schedulePlaceholder": "e.g., Tuesdays 6-8pm, flexible weekends",
    "volunteer.field.startDate": "Start date",
    "volunteer.field.endDate": "End date",
    "volunteer.field.endDateHelp": "Leave blank if ongoing",
    "volunteer.field.applicationDeadline": "Application deadline",
    "volunteer.field.volunteersNeeded": "Volunteers needed",
    "volunteer.field.minimumAge": "Minimum age",
    "volunteer.field.backgroundCheckRequired": "Background check required",
    "volunteer.field.trainingProvided": "Training provided",
    "volunteer.field.onePerLine": "one per line",
    "volunteer.validation.volunteersNeeded": "Enter a number of 1 or more",
    "volunteer.validation.minimumAge": "Enter an age between 0 and 120",
    "volunteer.validation.endDate": "End date must be on or after the start date",
    "volunteer.signInToApply":
      "Please sign in to apply for volunteer opportunities",
    "volunteer.applicationSuccess": "Application submitted successfully!",
    "volunteer.removeFilter": "Remove {{filter}} filter",

    // Volunteer - OpportunityForm
    "volunteer.validation.titleRequired": "Title is required",
    "volunteer.validation.descriptionRequired": "Description is required",
    "volunteer.validation.skillsRequired": "At least one skill is required",
    "volunteer.validation.locationRequired": "Location is required",
    "volunteer.profileNotFound": "User profile not found",
    "volunteer.correctErrors": "Please correct the validation errors",
    "volunteer.createFailed": "Failed to create opportunity",
    "volunteer.descriptionPlaceholder":
      "Describe the volunteer opportunity in detail...",
    "volunteer.skillsPlaceholder":
      "e.g., Web Development, Project Management, Translation",
    "volunteer.locationPlaceholder": "e.g., Remote, New York, Berlin",
    "volunteer.headerImage": "Header Image",
    "volunteer.headerImageHelp":
      "Upload an image to display at the top of your opportunity listing",
    "volunteer.limitReached": "Opportunity Limit Reached",
    "volunteer.limitReachedMessage":
      "You have reached the maximum of {{max}} active volunteer opportunities. Please close or complete an existing opportunity before creating a new one.",
    "volunteer.opportunityCount":
      "You have {{count}} of {{max}} active opportunities.",

    // Volunteer - HoursVerificationTab
    "volunteer.hoursVerification": "Hours Verification",
    "volunteer.hoursVerificationDescription":
      "Review and verify volunteer hour submissions",
    "volunteer.formalHours": "Logged Hours",
    "volunteer.selfReported": "Self-Reported",
    "volunteer.loggedHours": "Logged Volunteer Hours",
    "volunteer.loggedHoursDescription":
      "Hours logged by volunteers through your opportunities",
    "volunteer.allCaughtUp": "All caught up!",
    "volunteer.noPendingLoggedHours": "No pending logged hours to verify.",
    "volunteer.selfReportedHours": "Self-Reported Hours",

    // Volunteer - OpportunitiesTab
    "volunteer.noOpportunitiesTitle": "No opportunities yet",
    "volunteer.activeCount": "{{count}} of {{max}} active opportunities",
    "volunteer.moreSkills": "+{{count}} more",

    // Volunteer - ApplicationsTab
    "volunteer.noApplications": "No pending applications",
    "volunteer.unknownOpportunity": "Unknown Opportunity",

    // Volunteer - VerificationLookup
    "volunteer.verifyContribution": "Verify Volunteer Contribution",
    "volunteer.hashPlaceholder": "Enter verification hash",
    "volunteer.searching": "Searching...",
    "volunteer.verifyHash": "Verify Hash",
    "volunteer.verificationNotFound": "Verification Not Found",
    "volunteer.verificationNotFoundMessage":
      "The hash you provided could not be found in our records. Please check the hash and try again.",
    "volunteer.unknownOrganization": "Unknown Organization",

    // Volunteer - VolunteerVerificationCard
    "volunteer.contributionVerification": "Volunteer Contribution Verification",
    "volunteer.volunteerLabel": "Volunteer",
    "volunteer.opportunityLabel": "Opportunity",
    "volunteer.organizationLabel": "Organization",
    "volunteer.acceptedOn": "Accepted On",
    "volunteer.verifiedOn": "Verified On",
    "volunteer.verificationHashes": "Verification Hashes",
    "volunteer.blockchainReference": "Blockchain Reference",

    // Volunteer - VerifyContribution page
    "volunteer.verifyingContribution": "Verifying contribution\u2026",
    "volunteer.noHashProvided": "No verification hash provided.",
    "volunteer.verificationSuccessful": "Verification Successful",
    "volunteer.contributionVerifiedBlockchain":
      "This volunteer contribution has been verified and recorded on the blockchain.",
    "volunteer.verificationFailed": "Verification Failed",
    "volunteer.hashNotFound":
      "The verification hash {{hash}} could not be found or is invalid.",

    // Volunteer - SelfReportedHoursDashboard
    "volunteer.volunteerHoursTitle": "Volunteer Hours",
    "volunteer.aboutSelfReported": "About Self-Reported Hours",
    "volunteer.selfReportedInfo":
      "Only hours validated by verified organizations count toward the Global Impact Rankings. Hours for organizations not on our platform can be tracked but will be marked as \u201cUnvalidated\u201d until validation is received.",
    "volunteer.logHoursButton": "Log Hours",
    "volunteer.logVolunteerHours": "Log Volunteer Hours",
    "volunteer.editRecord": "Edit Record",
    "volunteer.recordDetails": "Record Details",
    "volunteer.backToList": "Back to List",
    "volunteer.deleteRecord": "Delete Record?",
    "volunteer.deleteConfirmation":
      "Are you sure you want to delete this volunteer hours record? This action cannot be undone.",

    // Volunteer - SelfReportedHoursForm
    "volunteer.activityType": "Activity Type",
    "volunteer.organization": "Organization",
    "volunteer.searchRegistry": "Search Registry",
    "volunteer.searchRegistryPlaceholder":
      "Search charity registry by name or tax ID\u2026",
    "volunteer.registryTaxId": "Tax ID {{value}}",
    "volunteer.notListed": "Not Listed",
    "volunteer.organizationName": "Organization Name",
    "volunteer.enterOrgName": "Enter organization name",
    "volunteer.contactEmail": "Contact Email",
    "volunteer.contactEmailOptional": "(optional)",
    "volunteer.onboardHelp":
      "We may reach out to help onboard this organization",
    "volunteer.submittedForValidation":
      "This record will be submitted for validation",
    "volunteer.submittedForValidationTo":
      "This record will be submitted for validation to {{org}}",
    "volunteer.validationExpired":
      "Validation period has expired for this date",
    "volunteer.savedAsUnvalidated": "This record will be saved as unvalidated",
    "volunteer.describeActivities": "Describe the activities you performed...",
    "volunteer.moreCharsNeeded": "{{count}} more characters needed",
    "volunteer.updating": "Updating...",
    "volunteer.logging": "Logging...",
    "volunteer.updateHours": "Update Hours",

    // Volunteer - SelfReportedHoursList
    "volunteer.statusFilter": "Status",
    "volunteer.activityTypeFilter": "Activity Type",
    "volunteer.filterFrom": "From",
    "volunteer.filterTo": "To",
    "volunteer.clearFilters": "Clear filters",
    "volunteer.allStatuses": "All",
    "volunteer.allActivityTypes": "All Types",
    "volunteer.noMatchingRecords": "No matching records",
    "volunteer.noHoursLogged": "No volunteer hours logged yet",
    "volunteer.adjustFilters": "Try adjusting your filters to find records.",
    "volunteer.startLogging": "Start by logging your first volunteer hours.",

    // Volunteer - SelfReportedHoursStats
    "volunteer.validatedHours": "Validated Hours",
    "volunteer.pendingValidation": "Pending Validation",
    "volunteer.unvalidatedHours": "Unvalidated Hours",
    "volunteer.rejectedLabel": "Rejected",
    "volunteer.hoursUnit": "hrs",
    "volunteer.recordSingular": "record",
    "volunteer.recordPlural": "records",

    // Volunteer - VolunteerApplicationForm
    "volunteer.applicationTitle": "Volunteer Opportunity Application",
    "volunteer.applicationSubtitle":
      "Help create sustainable impact through verified contributions",
    "volunteer.progress.info": "Info",
    "volunteer.progress.skills": "Skills",
    "volunteer.progress.consent": "Consent",
    "volunteer.fieldValid": "Looks good",
    "volunteer.draftSaved": "Draft saved",
    "volunteer.personalInfo": "Personal Information",
    "volunteer.firstName": "First Name",
    "volunteer.lastName": "Last Name",
    "volunteer.emailAddress": "Email Address",
    "volunteer.phoneNumber": "Phone Number",
    "volunteer.locationCity": "Location/City",
    "volunteer.locationCityPlaceholder": "e.g., San Francisco, CA",
    "volunteer.timeZone": "Time Zone",
    "volunteer.selectTimeZone": "Select Time Zone",
    "volunteer.ageRange": "Age Range",
    "volunteer.selectAgeRange": "Select Age Range",
    "volunteer.skillsAndInterests": "Skills & Interests",
    "volunteer.skillsLabel": "Skills and Areas of Interest",
    "volunteer.skillPlaceholderFull":
      "Start typing your skills (e.g., Python programming, Public speaking, Grant writing)",
    "volunteer.skillPlaceholderShort": "Type a skill and press Enter...",
    "volunteer.commitmentLevel": "Commitment Level",
    "volunteer.commitmentOneTimeDesc": "Single project or short-duration tasks",
    "volunteer.commitmentShortTermDesc": "Few weeks to a few months",
    "volunteer.commitmentLongTermDesc":
      "Ongoing commitment of several months or more",
    "volunteer.experienceLabel": "Tell us about your relevant experience",
    "volunteer.experiencePlaceholder":
      "Describe your background, skills, and what motivates you to volunteer with Give Protocol...",
    "volunteer.consentTitle": "Consent & Agreement",
    "volunteer.consentHeader": "Volunteer Application Consent",
    "volunteer.submitting": "Submitting...",
    "volunteer.submitApplication": "Submit Volunteer Application",
    "volunteer.disclaimerText":
      "By submitting this application, you acknowledge that you have read and understood Give Protocol\u2019s privacy policy and volunteer guidelines. Your data will be processed in accordance with applicable data protection regulations.",
    "volunteer.validation.firstNameInvalid": "Please enter a valid first name",
    "volunteer.validation.lastNameInvalid": "Please enter a valid last name",
    "volunteer.validation.emailInvalid": "Please enter a valid email address",
    "volunteer.validation.phoneInvalid": "Please enter a valid phone number",
    "volunteer.validation.experienceRequired":
      "Please describe your relevant experience",
    "volunteer.validation.addSkill": "Please add at least one skill",
    "volunteer.validation.ageRangeRequired": "Please select your age range",
    "volunteer.validation.consentRequired":
      "You must agree to all required consent items",
    "volunteer.signInToSubmit": "Please log in to submit an application",
    "volunteer.submitFailed": "Failed to submit application. Please try again.",

    // Volunteer - ApplicationForm
    "volunteer.applyForTitle": "Apply for: {{title}}",
    "volunteer.fullName": "Full Name *",
    "volunteer.phoneRequired": "Phone Number *",
    "volunteer.emailRequired": "Email Address *",
    "volunteer.dateOfBirth": "Date of Birth",
    "volunteer.availabilityTitle": "Availability",
    "volunteer.preferredDays": "Preferred Days",
    "volunteer.preferredTimes": "Preferred Times",
    "volunteer.commitmentLevelLabel": "Commitment Level",
    "volunteer.skillsAndExperience": "Skills & Experience",
    "volunteer.relevantExperience": "Relevant Experience *",
    "volunteer.certifications": "Certifications (comma-separated)",
    "volunteer.interestsAndPreferences": "Interests & Preferences",
    "volunteer.areasOfInterest": "Areas of Interest (comma-separated)",
    "volunteer.referencesTitle": "References",
    "volunteer.referenceName": "Reference {{index}} Name",
    "volunteer.referenceContact": "Reference {{index}} Contact",
    "volunteer.workSamplesTitle": "Work Samples",
    "volunteer.workSamplesLabel": "Links to Work Samples (comma-separated)",
    "volunteer.submitApplicationShort": "Submit Application",
    "volunteer.validation.nameInvalid":
      "Please enter a valid name (2-100 characters)",
    "volunteer.validation.provideExperience":
      "Please provide information about your experience",
    "volunteer.validation.selectDay": "Please select at least one day",
    "volunteer.validation.selectTime": "Please select at least one time",

    // Volunteer - ConsentForm
    "volunteer.consentUnderstand": "I understand that:",
    "volunteer.understanding.item1":
      "My personal information will be processed to evaluate my volunteer application, manage volunteer assignments, and carry out related activities. This processing is necessary to take steps at my request before, and to perform, any volunteer arrangement (GDPR Art. 6(1)(b)); it does not rely on my consent.",
    "volunteer.understanding.item2":
      "GIVE PROTOCOL may collect various categories of my personal information, including identity information, contact details, background information, availability, and references. I should not include special categories of data (such as information about health, racial or ethnic origin, religious or political beliefs, trade-union membership, or sex life or sexual orientation) in free-text fields, as such data is not required to evaluate my application.",
    "volunteer.understanding.item3":
      "My personal information may be shared with authorized personnel within the charity organization offering the volunteer opportunity, service providers, and third parties as outlined in the Privacy Notice.",
    "volunteer.understanding.item4":
      "Where a service provider is located outside my country of residence, my personal information may be transferred internationally under appropriate safeguards (such as Standard Contractual Clauses) as described in the Privacy Notice. These transfers do not rely on my consent.",
    "volunteer.understanding.item5":
      "I have certain rights regarding my personal information, which vary depending on my location, including the rights to access, rectify, delete, restrict processing, data portability, and object to processing.",
    "volunteer.understanding.item6Before":
      "I can object to processing (GDPR Art. 21) and request erasure of my personal information (GDPR Art. 17) by contacting",
    "volunteer.understanding.item6After":
      ". Where any specific processing is based on my consent, I can withdraw that consent at any time without affecting the lawfulness of processing carried out before withdrawal. Objecting or requesting erasure may impact the organisation\u2019s ability to consider my volunteer application.",
    "volunteer.processingAckTitle": "PROCESSING ACKNOWLEDGMENT",
    "volunteer.processingAckDesc":
      "Please review and acknowledge the following before continuing:",
    "volunteer.essentialProcessingTitle":
      "Application Processing (Required to confirm):",
    "volunteer.acknowledgmentTitle": "ACKNOWLEDGMENT",
    "volunteer.ageConfirmationTitle": "Age Confirmation:",
    "volunteer.privacyNoticeTitle": "Privacy Notice:",
    "volunteer.doNotAccept": "Do Not Accept",
    "volunteer.acceptAndContinue": "Accept and Continue",
    "volunteer.validation.essentialRequired":
      "You must confirm the application-processing acknowledgment to proceed",
    "volunteer.validation.agePrivacyRequired":
      "You must confirm your age and that you have read the Privacy Notice",

    // Age affirmation gate (shared across all surfaces — GIV-453)
    "ageGate.positive": "I confirm I am 16 years of age or older.",
    "ageGate.negative":
      "Give Protocol is available to users who are 16 years of age or older. If you are under 16, we are unable to process your request at this time.",

    // Languages
    "language.english": "English",
    "language.spanish": "Spanish",
    "language.german": "German",
    "language.french": "French",
    "language.japanese": "Japanese",
    "language.chinese_simplified": "Chinese (Simplified)",
    "language.chinese_traditional": "Chinese (Traditional)",
    "language.thai": "Thai",
    "language.vietnamese": "Vietnamese",
    "language.korean": "Korean",
    "language.arabic": "Arabic",
    "language.hindi": "Hindi",
    "language.multiple": "Multiple Languages",

    // Contribution types
    "contribution.type.donation": "Donation",
    "contribution.type.volunteerapplication": "Volunteer Application",
    "contribution.type.volunteerhours": "Volunteer Hours",
    "contribution.type.volunteeracceptance": "Volunteer Acceptance",
    "contribution.type.volunteerverification": "Volunteer Verification",

    // Donor/Volunteer
    "donor.volunteer": "Donor/Volunteer",
    "donor.anonymous": "Anonymous",
    "volunteer.unknown": "Unknown Volunteer",

    // Statuses
    "status.completed": "Completed",
    "status.pending": "Pending",
    "status.failed": "Failed",

    // Blockchain
    "blockchain.block": "Block",

    // Filter
    "filter.allYears": "All Years",
    "filter.allTypes": "All Types",
    "filter.donations": "Donations",
    "filter.volunteerApplications": "Volunteer Applications",
    "filter.volunteerHours": "Volunteer Hours",
    "filter.fiatDonations": "Fiat Donations",

    // Export
    "export.title": "Export Contribution Data",
    "export.filename": "Filename",
    "export.dateRange": "Date Range",
    "export.includePersonal": "Include wallet addresses",
    "export.willInclude": "The export will include:",
    "export.walletAddresses": "Wallet addresses (sender and recipient)",
    "export.volunteerDetails":
      "Volunteer contribution details (when applicable)",
    "export.verificationHashes": "Verification hashes (when applicable)",
    "export.cancel": "Cancel",
    "export.download": "Export CSV",

    // Skills
    "skills.endorsements": "Skills & Endorsements",

    // Home
    "home.subtitle": "Connecting resources to the causes that need them most",
    "home.startDonating": "Start Donating",

    // Modal - shared
    "modal.close": "Close modal",
    "modal.closeAriaLabel": "Close",

    // Modal - wallet connection
    "modal.wallet.title": "Connect Your Wallet",
    "modal.wallet.description":
      "Connect a wallet to unlock all features of Give Protocol",
    "modal.wallet.benefit1":
      "Track your donations transparently on the blockchain",
    "modal.wallet.benefit2":
      "Receive GIVE tokens as rewards for your contributions",
    "modal.wallet.benefit3":
      "Participate in governance decisions for the protocol",
    "modal.wallet.connecting": "Connecting...",
    "modal.wallet.clickToConnect": "Click to connect",
    "modal.wallet.noExtension": "No wallet extension detected.",
    "modal.wallet.installMetaMask": "Install MetaMask to get started",
    "modal.wallet.skip": "Skip for now",
    "modal.wallet.footerNote":
      "You can connect your wallet anytime from the navigation bar",
    "modal.wallet.failedConnect": "Failed to connect wallet",

    // Modal - wallet link
    "modal.walletLink.title": "Link Your Wallet",
    "modal.walletLink.description":
      "Link a wallet to your account for on-chain donations, token rewards, and governance",
    "modal.walletLink.benefit1":
      "Donate crypto directly to verified charities on-chain",
    "modal.walletLink.benefit2Pre": "Develop",
    "modal.walletLink.benefit2Post": "credentials for your contributions",
    "modal.walletLink.benefit3":
      "Sign in with either email or wallet \u2014 same account",
    "modal.walletLink.buttonLinking": "Linking\u2026",
    "modal.walletLink.buttonLink": "Link Wallet",
    "modal.walletLink.buttonConnectFirst": "Connect Wallet First",
    "modal.walletLink.footerNote":
      "You can link your wallet anytime from Settings",
    "modal.walletLink.failedLink": "Failed to link wallet",

    // Modal - chain selection
    "modal.chain.title": "Welcome to Give Protocol",
    "modal.chain.subtitle": "Choose your preferred network to get started",
    "modal.chain.footerNote": "You can switch networks anytime from the menu",
    "modal.chain.connecting": "Connecting...",
    "modal.chain.continue": "Continue",
    "modal.chain.failedSelect": "Failed to select network",

    // Modal - network selection (WalletModal step 1)
    "modal.network.title": "Select Network",
    "modal.network.nextStep": "Next Step: Connect Wallet",
    "modal.network.learnMore": "Learn more about network selection",

    // Modal - wallet connect (WalletModal step 2)
    "modal.connect.title": "Connect Wallet",
    "modal.connect.backAria": "Back to network selection",
    "modal.connect.noWallets": "No wallets available for {{chainType}} chains.",
    "modal.connect.tryDifferentChain": "Try selecting a different chain type.",
    "modal.connect.viewOtherWallets": "View other supported wallets",
    "modal.connect.termsAgreement": "By connecting, you agree to our",
    "modal.connect.termsLink": "Terms of Service",
    "modal.connect.termsPrivacyConnector": "and",
    "modal.connect.privacyLink": "Privacy Policy",
    "modal.connect.chainTypeAria": "Chain type",
    "modal.connect.failedConnect": "Connection failed",

    // Modal - donation
    "modal.donation.successTitle": "Thank You!",
    "modal.donation.donationProcessedOneTime":
      "Your donation of {{amount}} to {{charityName}} has been processed.",
    "modal.donation.donationProcessedRecurring":
      "Your monthly donation of {{amount}} to {{charityName}} has been processed.",
    "modal.donation.recurringNote":
      "You\u2019ll be charged monthly until you cancel.",
    "modal.donation.receiptNote": "A receipt has been sent to your email.",
    "modal.donation.done": "Done",
    "modal.donation.errorTitle": "Something Went Wrong",
    "modal.donation.errorDefault":
      "An unexpected error occurred. Please try again.",
    "modal.donation.tryAgain": "Try Again",
    "modal.donation.close": "Close",
    "modal.donation.amountLabel": "Donation Amount",
    "modal.donation.perMonth": "per month",
    "modal.donation.monthly": "Monthly",
    "modal.donation.oneTime": "One-Time",
    "modal.donation.loadingPaymentForm": "Loading payment form...",
    "modal.donation.supportMonthly": "Support {{charityName}} Monthly",
    "modal.donation.donateToCharity": "Donate to {{charityName}}",

    // Modal - scheduled donation
    "modal.scheduledDonation.title":
      "Schedule Monthly Donations to {{charityName}}",

    // Modal - withdrawal
    "modal.withdrawal.title": "Request Withdrawal",

    // Modal - validation response
    "modal.validation.rejectTitle": "Reject Validation Request",
    "modal.validation.reviewTitle": "Review Validation Request",
    "modal.validation.volunteer": "Volunteer",
    "modal.validation.activityDetails": "Activity Details",
    "modal.validation.date": "Date",
    "modal.validation.hour": "hour",
    "modal.validation.hours": "hours",
    "modal.validation.activityType": "Activity Type",
    "modal.validation.location": "Location",
    "modal.validation.description": "Description",
    "modal.validation.submitted": "Submitted:",
    "modal.validation.daysRemaining": "({{count}} days remaining to validate)",
    "modal.validation.appealNote":
      "This is an appeal/resubmission of a previously rejected request",
    "modal.validation.rejectPrompt":
      "Please select a reason for rejecting this validation request. The volunteer will be notified.",
    "modal.validation.reject": "Reject",
    "modal.validation.approve": "Approve",
    "modal.validation.processing": "Processing...",
    "modal.validation.back": "Back",
    "modal.validation.confirmRejection": "Confirm Rejection",
    "modal.validation.selectReasonError": "Please select a rejection reason",

    // Toast
    "toast.dismiss": "Dismiss notification",

    // Error boundary
    "error.somethingWrong": "Something went wrong",
    "error.unexpectedError": "An unexpected error occurred",
    "error.tryAgain": "Try Again",
    "error.tooManyAttempts": "Too many attempts",
    "error.goHome": "Go to Homepage",
    "error.details": "Error Details",

    // Validation - rejection reason select
    "validation.rejection.reasonLabel": "Rejection Reason",
    "validation.rejection.reasonPlaceholder": "Select a reason...",
    "validation.rejection.notesLabel": "Additional Notes",
    "validation.rejection.optional": "(optional)",
    "validation.rejection.notesPlaceholder":
      "Provide additional context for the rejection...",

    // Validation - batch actions
    "validation.batch.deselectAll": "Deselect all",
    "validation.batch.selectAll": "Select all",
    "validation.batch.selected": "{{selected}} of {{total}} selected",
    "validation.batch.clear": "Clear",
    "validation.batch.reject": "Reject ({{count}})",
    "validation.batch.approve": "Approve ({{count}})",

    // Dashboard - admin redirect (GIV-254)
    "dashboard.adminTitle": "Admin Dashboard",
    "dashboard.adminNote": "Please use the admin panel to manage the platform.",
    "dashboard.adminButton": "Go to Admin Panel",
    "dashboard.logVolunteerHours": "Log Volunteer Hours",
    "dashboard.hideVolunteerHours": "Hide Volunteer Hours",
    "dashboard.viewMonthlyDonations": "View Monthly Donations",
    "dashboard.hideMonthlyDonations": "Hide Monthly Donations",
    "dashboard.walletSettings": "Wallet Settings",
    "dashboard.overview": "Overview",
    "dashboard.lastUpdated": "Last updated",
    "dashboard.refreshData": "Refresh data",
    "dashboard.searchPlaceholder": "Search projects, volunteers (Cmd+K)",
    "dashboard.search": "Search",

    // Settings - account section (GIV-254)
    "settings.manageDescription": "Manage your account and preferences",
    "settings.account": "Account",
    "settings.email": "Email",
    "settings.displayName": "Display name",
    "settings.authMethod": "Auth method",
    "settings.role": "Role",

    // Common additional (GIV-254)
    "common.refresh": "Refresh",
    "common.all": "All",
    "common.loading": "Loading...",
    "common.close": "Close",
    "common.back": "Back",

    // Cause (GIV-254)
    "cause.createCause": "Create Cause",

    // Contribution Tracker (GIV-254)
    "tracker.title": "Global Impact Rankings",
    "tracker.subtitle":
      "Track and celebrate the collective impact of our community",
    "tracker.searchPlaceholder": "Search contributors...",
    "tracker.exportCSV": "Export CSV",
    "tracker.exportPDF": "Export PDF",
    "tracker.hideContributions": "Hide my contributions from rankings",
    "tracker.yourAlias": "Your alias:",
    "tracker.changeAlias": "Change",
    "tracker.setWalletAlias": "Set Wallet Alias",
    "tracker.aliasTitle": "Set Wallet Alias",
    "tracker.aliasDescription":
      "Your alias will be displayed on the contribution tracker instead of your wallet address.",
    "tracker.aliasLabel": "Alias",
    "tracker.aliasPlaceholder": "Enter your preferred alias",
    "tracker.saveAlias": "Save Alias",
    "tracker.donationRankings": "Donation Rankings",
    "tracker.volunteerRankings": "Volunteer Rankings",
    "tracker.exportComplete": "Export Complete",
    "tracker.exportDonationCSV": "Donation leaderboard exported as CSV",
    "tracker.exportVolunteerCSV": "Volunteer leaderboard exported as CSV",
    "tracker.exportPDFSuccess": "Leaderboard exported as PDF",
    "tracker.exportFailed": "Export Failed",
    "tracker.exportNoData": "No data available to export",
    "tracker.exportError": "An error occurred while exporting data",
    "tracker.walletNotConnected": "Wallet not connected",
    "tracker.connectWalletForAlias":
      "Please connect your wallet to set an alias",
    "tracker.invalidAlias": "Invalid alias",
    "tracker.enterValidAlias": "Please enter a valid alias",

    // Charity Portal (GIV-254)
    "charity.portal.deleteTitle": "Delete {{type}}?",
    "charity.portal.deleteDescription":
      "This action cannot be undone. The {{type}} will be permanently removed.",
    "charity.portal.walletNotConfigured": "Receiving wallet not configured.",
    "charity.portal.walletDonationNote":
      "Connect a wallet to receive on-chain donations.",
    "charity.portal.setupWallet": "Set Up Wallet",

    // Charity dashboard onboarding & verification
    "charity.onboarding.ariaLabel": "Onboarding checklist",
    "charity.onboarding.title": "Getting Started",
    "charity.onboarding.progress": "{{completed}} of {{total}} steps complete",
    "charity.onboarding.optional": "Optional",
    "charity.onboarding.allComplete":
      "All steps complete! You can dismiss this checklist.",
    "charity.onboarding.expand": "Expand checklist",
    "charity.onboarding.collapse": "Collapse checklist",
    "charity.onboarding.dismiss": "Dismiss onboarding checklist",
    "charity.onboarding.check": "Check {{label}}",
    "charity.onboarding.uncheck": "Uncheck {{label}}",
    "charity.onboarding.stepDone": "{{label}} (completed)",
    "charity.onboarding.stepAuto": "{{label}} (completes automatically)",
    "charity.onboarding.goToOrganization": "Go to Organization",
    "charity.onboarding.setupWallet": "Set up wallet",
    "charity.onboarding.item.completeProfile.label":
      "Complete organization profile",
    "charity.onboarding.item.completeProfile.description":
      "Add your organization name, description, address, and contact info.",
    "charity.onboarding.item.uploadLogo.label": "Upload logo or banner image",
    "charity.onboarding.item.uploadLogo.description":
      "Add a logo or banner to help donors recognize your charity.",
    "charity.onboarding.item.connectWallet.label": "Set up receiving wallet",
    "charity.onboarding.item.connectWallet.description":
      "Choose a multisig Safe, institutional custody, or single-signer wallet to receive donations. Prove control by signing a message.",
    "charity.onboarding.item.bankDetails.label":
      "Set up bank details for fiat off-ramp",
    "charity.onboarding.item.bankDetails.description":
      "Configure banking info if you want to accept card donations.",
    "charity.onboarding.item.acceptTerms.label":
      "Review and accept terms of service",
    "charity.onboarding.item.acceptTerms.description":
      "Read and confirm the Give Protocol charity terms and conditions.",
    "charity.verification.pendingTitle": "Application Under Review",
    "charity.verification.pendingBody":
      "Your charity application is being reviewed by our team. This typically takes 3–5 business days. We will email you when a decision is made.",
    "charity.verification.rejectedTitle": "Application Not Approved",
    "charity.verification.reason": "Reason: {{notes}}",
    "charity.verification.rejectedDefault":
      "Your application was not approved at this time.",
    "charity.verification.contactSupport": "Contact Support",
    "charity.verification.suspendedTitle": "Account Suspended",
    "charity.verification.suspendedDefault":
      "Your charity account has been suspended.",
    "charity.verification.appealSuspension": "Appeal Suspension",
    "charity.verification.verifiedTitle": "Charity Verified",
    "charity.verification.verifiedBody":
      "Your organization is verified and donors can now support your causes.",
    "charity.portal.receivingWallet": "Receiving wallet: {{address}}",
    "charity.portal.loadError":
      "Failed to load charity data. Please try again.",

    // Wallet Setup (GIV-289)
    "wallet.setup.title": "Set up your receiving wallet",
    "wallet.setup.subtitle":
      "Choose how your charity will receive crypto donations. We recommend a multisig wallet for maximum security.",
    "wallet.setup.walletsTitle": "Receiving wallets",
    "wallet.setup.addAnother": "Add another",
    "wallet.setup.recommended": "Recommended",
    "wallet.setup.safeTitle": "Multisig Safe",
    "wallet.setup.safeDesc":
      "Multiple signers required for every transaction. Highest security for organizational funds.",
    "wallet.setup.institutionalTitle": "Institutional Custody",
    "wallet.setup.institutionalDesc":
      "Managed by a qualified custodian (Fireblocks, Anchorage, etc.). Requires attestation document.",
    "wallet.setup.eoaTitle": "Single Signer (EOA)",
    "wallet.setup.eoaDesc":
      "A standard wallet controlled by one private key. Simplest but least secure for organizational use.",
    "wallet.setup.primary": "Primary",
    "wallet.setup.safeSigners": "Signers",
    "wallet.setup.custodian": "Custodian",
    "wallet.setup.viewExplorer": "View on block explorer",
    "wallet.setup.makePrimary": "Make primary",
    "wallet.setup.remove": "Remove wallet",
    "wallet.setup.walletAdded": "Wallet registered successfully",
    "wallet.setup.primaryUpdated": "Primary wallet updated",
    "wallet.setup.primaryFailed": "Failed to update primary wallet",
    "wallet.setup.walletDeleted": "Wallet removed",
    "wallet.setup.deleteFailed": "Failed to remove wallet",

    // Wallet - Safe flow (GIV-289)
    "wallet.safe.title": "Set up a Safe multisig wallet",
    "wallet.safe.subtitle":
      "A Safe requires multiple signers to approve every transaction, providing the highest level of security for your charity's funds.",
    "wallet.safe.createNew": "Create a new Safe",
    "wallet.safe.createNewDesc":
      "Opens app.safe.global where you can create a new multisig. Come back here once it's deployed.",
    "wallet.safe.haveOne": "I already have a Safe",
    "wallet.safe.haveOneDesc":
      "Enter your Safe address and verify control by signing a message from a signer wallet.",
    "wallet.safe.verifyTitle": "Verify Safe ownership",
    "wallet.safe.chain": "Network",
    "wallet.safe.address": "Safe address",
    "wallet.safe.signerWallet": "Signer wallet",
    "wallet.safe.connectSigner": "Connect signer wallet",
    "wallet.safe.signVerify": "Sign to verify control",
    "wallet.safe.verifying": "Verifying...",
    "wallet.safe.invalidAddress":
      "Please enter a valid Ethereum address (0x...)",
    "wallet.safe.connectFirst": "Please connect your signer wallet first.",
    "wallet.safe.rejected": "Signature request was rejected.",
    "wallet.safe.signError":
      "Failed to sign verification message. Please try again.",
    "wallet.safe.detecting": "Detecting Safe configuration...",
    "wallet.safe.signerCount": "Total signers (min 2)",
    "wallet.safe.signerThreshold": "Threshold (min 1)",

    // Wallet - Institutional flow (GIV-289)
    "wallet.institutional.title": "Register an institutional custody wallet",
    "wallet.institutional.subtitle":
      "Provide your custodian-managed wallet details and upload an attestation document for verification.",
    "wallet.institutional.chain": "Network",
    "wallet.institutional.address": "Wallet address",
    "wallet.institutional.custodian": "Custodian",
    "wallet.institutional.selectCustodianOption": "Select custodian...",
    "wallet.institutional.attestation": "Attestation document",
    "wallet.institutional.attestationHelp":
      "Upload a PDF, JPEG, or PNG (max 10MB) proving your custodial relationship.",
    "wallet.institutional.uploadFile": "Upload attestation file",
    "wallet.institutional.removeFile": "Remove file",
    "wallet.institutional.chooseFile": "Choose file or drag & drop",
    "wallet.institutional.submit": "Submit for review",
    "wallet.institutional.submitting": "Submitting...",
    "wallet.institutional.submittedTitle": "Wallet submitted for review",
    "wallet.institutional.submittedDesc":
      "Your institutional wallet registration is under review. You will receive an email within 3 business days.",
    "wallet.institutional.backToWallets": "Back to wallets",
    "wallet.institutional.invalidAddress":
      "Please enter a valid Ethereum address (0x...)",
    "wallet.institutional.selectCustodian": "Please select a custodian.",
    "wallet.institutional.uploadRequired":
      "Please upload an attestation document.",
    "wallet.institutional.invalidFileType":
      "Please upload a PDF, JPEG, or PNG file.",
    "wallet.institutional.fileTooLarge": "File must be smaller than 10MB.",

    // Wallet - EOA / Single-signer flow (GIV-289)
    "wallet.eoa.riskTitle": "Single-signer wallet: important risks",
    "wallet.eoa.riskParagraph1":
      "A single-signer (EOA) wallet is controlled by one private key. If that key is lost, stolen, or compromised, all funds sent to this wallet are permanently unrecoverable.",
    "wallet.eoa.riskParagraph2":
      "For organizational funds, we strongly recommend using a multisig Safe wallet instead. Multisig wallets require multiple approvals for every transaction, protecting against single points of failure.",
    "wallet.eoa.riskParagraph3":
      "By proceeding, you acknowledge that Give Protocol is not responsible for any loss of funds due to private key compromise, loss, or mismanagement.",
    "wallet.eoa.useMultisig": "Use a multisig instead",
    "wallet.eoa.proceedAnyway": "I understand, proceed",
    "wallet.eoa.signTitle": "Connect and sign",
    "wallet.eoa.signSubtitle":
      "Connect your wallet and sign a message to prove you control this address.",
    "wallet.eoa.chain": "Network",
    "wallet.eoa.walletConnection": "Wallet",
    "wallet.eoa.connectWallet": "Connect wallet",
    "wallet.eoa.signMessage": "Sign verification message",
    "wallet.eoa.connectFirst": "Please connect your wallet first.",
    "wallet.eoa.rejected": "Signature request was rejected.",
    "wallet.eoa.signError": "Failed to sign message. Please try again.",
    "wallet.eoa.confirmTitle": "Final confirmation",
    "wallet.eoa.confirmSubtitle":
      "Please confirm the following before registering this wallet.",
    "wallet.eoa.walletToRegister": "Wallet to register",
    "wallet.eoa.checkRisk":
      "I understand that a single-signer wallet carries higher risk than a multisig, and that loss of the private key means permanent loss of funds.",
    "wallet.eoa.checkAuthorized":
      "I am authorized by my organization to designate this wallet as a receiving address for charitable donations.",
    "wallet.eoa.register": "Register wallet",
    "wallet.eoa.registering": "Registering...",
    "wallet.eoa.missingSignature":
      "Missing signature. Please go back and sign again.",

    // Timestamps (GIV-254)
    "timestamp.justNow": "Just now",
    "timestamp.oneMinuteAgo": "1 minute ago",
    "timestamp.minutesAgo": "{{count}} minutes ago",

    // Common additions (GIV-256)
    "common.previous": "Previous",
    "common.next": "Next",
    "common.pageOfTotal": "Page {{page}} of {{total}}",
    "common.retry": "Retry",
    "common.processing": "Processing...",
    "common.saving": "Saving...",
    "common.donate": "Donate",
    "common.active": "Active",

    // Portfolio Funds - public page (GIV-256)
    "portfolio.donationType": "Donation Type",
    "portfolio.devNative": "DEV (Native)",
    "portfolio.testToken": "TEST Token",
    "portfolio.donationAmount": "Donation Amount:",
    "portfolio.platformFee": "Platform Fee ({{percentage}}%):",
    "portfolio.toCharities": "To Charities:",
    "portfolio.eachCharityReceives": "Each charity receives:",
    "portfolio.donateToFund": "Donate to {{name}}",
    "portfolio.equalDistribution": "Equal Distribution",
    "portfolio.equalDistributionNote":
      "Your donation will be split equally among {{count}} verified charities.",
    "portfolio.eachCharityPercentage":
      "Each charity receives {{percentage}}% of donations",
    "portfolio.verifiedCharities": "{{count}} Verified Charities",
    "portfolio.donateToFundButton": "Donate to Fund",
    "portfolio.noFundsAvailable": "No portfolio funds available",
    "portfolio.checkBackLater":
      "Check back later for new funding opportunities",
    "portfolio.amountLabel": "Amount ({{currency}})",
    "portfolio.invalidAmount": "Please enter a valid amount",
    "portfolio.donationSuccess": "Donation successful!",
    "portfolio.donationFailed": "Donation failed. Please try again.",

    // Admin - Activity event type labels (GIV-256)
    "admin.activity.donation": "Donation",
    "admin.activity.registration": "Registration",
    "admin.activity.verification": "Verification",
    "admin.activity.volunteerHours": "Volunteer Hours",
    "admin.activity.default": "Activity",

    // Admin - Dashboard page (GIV-256)
    "admin.dashboard.title": "Admin Dashboard",
    "admin.dashboard.alerts": "Alerts",
    "admin.dashboard.alertsPending": "{{count}} pending",
    "admin.dashboard.alertsView": "View",
    "admin.dashboard.recentActivity": "Recent Activity",
    "admin.dashboard.noRecentActivity": "No recent activity.",
    "admin.dashboard.quickActions": "Quick Actions",
    "admin.dashboard.errorTitle": "Error Loading Dashboard",
    "admin.error.title": "Data Load Error",
    "admin.error.recentActivity": "Recent Activity Load Error",
    "admin.error.alerts": "Alerts Load Error",
    "admin.error.donationVolume": "Donation Volume Load Error",
    "admin.error.charities": "Charities Load Error",
    "admin.error.donations": "Donations Load Error",
    "admin.stats.last7d": "last 7d",
    "admin.stats.last30d": "last 30d",
    "admin.stats.totalDonors": "Total Donors",
    "admin.stats.newRegistrations": "new registrations",
    "admin.stats.charities": "Charities",
    "admin.stats.pendingVerification": "pending verification",
    "admin.stats.verifiedCharities": "Verified Charities",
    "admin.stats.activeVolunteers": "Active Volunteers",
    "admin.stats.totalDonationVolume": "Total Donation Volume",
    "admin.stats.cryptoVolume": "Crypto Volume",
    "admin.stats.fiatVolume": "Fiat Volume",
    "admin.stats.donations": "donations",

    // Admin - Quick Actions (GIV-256)
    "admin.actions.viewReports": "View Reports",
    "admin.actions.viewReportsDesc": "Generate detailed analytics",
    "admin.actions.manageCharities": "Manage Charities",
    "admin.actions.manageCharitiesDesc": "Review and approve organizations",
    "admin.actions.systemSettings": "System Settings",
    "admin.actions.systemSettingsDesc": "Configure platform parameters",
    "admin.actions.manageImpactMetrics": "Manage Impact Metrics",
    "admin.actions.manageImpactMetricsDesc": "Configure impact calculator data",
    "admin.actions.donationMonitoring": "Donation Monitoring",
    "admin.actions.donationMonitoringDesc":
      "Monitor, flag, and report on donations",
    "admin.actions.contentModeration": "Content Moderation",
    "admin.actions.contentModerationDesc":
      "Hide, flag, and review opportunities and causes",
    "admin.actions.portfolioFunds": "Portfolio Funds",
    "admin.actions.portfolioFundsDesc":
      "Create and manage curated giving portfolios",
    "admin.actions.charityRequests": "Charity Requests",
    "admin.actions.charityRequestsDesc":
      "Review donor requests for unclaimed charities",
    "admin.actions.platformNews": "Platform News",
    "admin.actions.platformNewsDesc": "Manage news items on the browse page",

    // Admin console redesign — operations shell (sidebar + top bar)
    "admin.nav.dashboard": "Dashboard",
    "admin.nav.reports": "Reports & Analytics",
    "admin.nav.charities": "Charities",
    "admin.nav.donations": "Donations",
    "admin.nav.impactMetrics": "Impact Metrics",
    "admin.nav.portfolioFunds": "Portfolio Funds",
    "admin.nav.contentModeration": "Content Moderation",
    "admin.nav.settings": "System Settings",
    "admin.nav.overview": "Overview",
    "admin.nav.operations": "Operations",
    "admin.nav.consoleLabel": "Admin Console",
    "admin.shell.searchPlaceholder": "Search donors, charities, tx hash…",
    "admin.shell.searchLabel": "Search the admin console",
    "admin.shell.notifications": "Notifications",
    "admin.shell.adminLabel": "admin",
    "admin.shell.superAdmin": "Super Admin",
    "admin.shell.signOut": "Sign out",
    "admin.shell.backToPublicSite": "Back to public site",
    "admin.shell.homeLink": "Give Protocol home",

    // Admin console redesign — Dashboard
    "admin.dashboard.severityHigh": "High Priority",
    "admin.dashboard.severityMedium": "Needs Attention",
    "admin.dashboard.severityLow": "For Review",
    "admin.dashboard.alertOldest": "Oldest request submitted",
    "admin.dashboard.alertReviewHint": "review to keep onboarding moving",
    "admin.dashboard.reviewQueue": "Review queue",
    "admin.dashboard.kpiDonorsSub": "{{count}} new in last 30 days",
    "admin.dashboard.kpiPending": "{{count}} pending",
    "admin.dashboard.awaitingVerification": "Awaiting verification",
    "admin.dashboard.kpiVerifiedSub": "of {{total}} total approved",
    "admin.dashboard.kpiVolunteersActive": "Active sign-ups",
    "admin.dashboard.kpiVolunteersSub": "No active sign-ups",
    "admin.dashboard.donationVolume": "Donation Volume",
    "admin.dashboard.donationVolumeSub": "{{range}} · crypto + fiat combined",
    "admin.dashboard.range30d": "Last 30 days",
    "admin.dashboard.range90d": "Last 90 days",
    "admin.dashboard.rangeYtd": "Year to date",
    "admin.dashboard.volumeEmptyTitle": "No donations recorded yet",
    "admin.dashboard.volumeEmptySub":
      "Volume will chart here once the first gift settles",
    "admin.dashboard.totalVolume": "Total volume",
    "admin.dashboard.crypto": "Crypto",
    "admin.dashboard.fiat": "Fiat",
    "admin.dashboard.viewAllDonations": "View all donations",
    "admin.dashboard.activitySubtitle": "Platform events as they happen",
    "admin.dashboard.viewAllActivity": "View all activity",

    // Admin - GDPR erasure cron status (GIV-864 F4 / Finding #23)
    "admin.dashboard.gdprCronTitle": "GDPR Erasure Cron",
    "admin.dashboard.gdprCronScheduleDesc":
      "Nightly at 02:00 UTC (pg_cron · Art. 17 & Art. 5(1)(e))",
    "admin.dashboard.gdprCronLastRun": "Last run: {{time}}",
    "admin.dashboard.gdprTotalErasures": "{{count}} erased total",
    "admin.dashboard.gdprPendingDue": "{{count}} erasure due",
    "admin.dashboard.gdprZeroPending": "0 pending erasures",
    "admin.dashboard.viewHealthReport": "Platform Health →",
    "admin.dashboard.gdprCronChecking": "Checking GDPR erasure cron status...",
    "admin.dashboard.cronActive": "Active / Scheduled",
    "admin.dashboard.cronFailed": "Last Run Failed",
    "admin.dashboard.cronInactive": "Inactive",

    // Admin console redesign — Charity list view
    "admin.charity.tabAll": "All",
    "admin.charity.review": "Review",
    "admin.charity.colOrganization": "Organization",
    "admin.charity.colRaised": "Raised",
    "admin.charity.colSubmitted": "Submitted",
    "admin.charity.searchByNameEin": "Search by name or tax ID…",
    "admin.charity.searchAria": "Search charities",
    "admin.charity.reviewTitle": "Review {{name}}",
    "admin.charity.submittedRelative": "Submitted {{when}}",
    "admin.charity.contactEmail": "Email",
    "admin.charity.contactPhone": "Phone",
    "admin.charity.raisedUnavailable": "No donation total available",

    // Admin - Platform News management page (GIV-279)
    "admin.news.title": "Platform News",
    "admin.news.subtitle": "Manage news items shown on the public browse page",
    "admin.news.newItem": "New Item",
    "admin.news.editNews": "Edit News Item",
    "admin.news.createNews": "Create News Item",
    "admin.news.titleLabel": "Title",
    "admin.news.titlePlaceholder": "e.g. Quarterly impact report",
    "admin.news.contentLabel": "Content / Excerpt",
    "admin.news.contentPlaceholder": "Brief description of the news item...",
    "admin.news.urlLabel": "Link URL",
    "admin.news.urlPlaceholder": "https://example.org/article",
    "admin.news.imageLabel": "Image URL (optional)",
    "admin.news.imagePlaceholder": "https://...",
    "admin.news.categoryLabel": "Category",
    "admin.news.dateLabel": "Publish Date",
    "admin.news.activeLabel": "Published (visible to public)",
    "admin.news.saving": "Saving...",
    "admin.news.createNewsBtn": "Create News",
    "admin.news.updateNews": "Update News",
    "admin.news.noItemsYet": "No news items yet",
    "admin.news.noItemsMessage":
      "Create your first news item to share updates with your community",
    "admin.news.publishedOn": "Published {{date}}",
    "admin.news.inactive": "Inactive",
    "admin.news.deactivate": "Deactivate {{title}}",
    "admin.news.activate": "Activate {{title}}",
    "admin.news.editItem": "Edit {{title}}",
    "admin.news.deleteItem": "Delete {{title}}",
    "admin.news.deactivated": "News item hidden",
    "admin.news.activated": "News item published",
    "admin.news.deleted": "News item deleted",
    "admin.news.updated": "News item updated",
    "admin.news.created": "News item created",

    // Admin - Portfolio Funds management page (GIV-256)
    "admin.portfolio.title": "Portfolio Funds",
    "admin.portfolio.subtitle": "Create and manage curated giving portfolios",
    "admin.portfolio.newFund": "New Fund",
    "admin.portfolio.editFund": "Edit Fund",
    "admin.portfolio.createNewFund": "Create New Fund",
    "admin.portfolio.fundName": "Fund Name",
    "admin.portfolio.fundNamePlaceholder": "e.g. Environmental Impact Fund",
    "admin.portfolio.description": "Description",
    "admin.portfolio.descriptionPlaceholder":
      "Describe the fund's purpose and impact focus...",
    "admin.portfolio.category": "Category",
    "admin.portfolio.selectCategory": "Select a category",
    "admin.portfolio.imageUrl": "Image URL",
    "admin.portfolio.imageUrlPlaceholder": "https://...",
    "admin.portfolio.selectCharities": "Select Charities ({{count}} selected)",
    "admin.portfolio.noCharitiesFound": "No verified charities found",
    "admin.portfolio.saving": "Saving...",
    "admin.portfolio.createFund": "Create Fund",
    "admin.portfolio.updateFund": "Update Fund",
    "admin.portfolio.noFundsYet": "No portfolio funds yet",
    "admin.portfolio.noFundsMessage":
      "Create your first fund to group charities for bundled donations",
    "admin.portfolio.charity": "charity",
    "admin.portfolio.charities": "charities",
    "admin.portfolio.createdOn": "Created {{date}}",
    "admin.portfolio.statusActive": "active",
    "admin.portfolio.statusPaused": "paused",
    "admin.portfolio.statusArchived": "archived",
    "admin.portfolio.errorLoad": "Failed to load data",
    "admin.portfolio.errorArchive": "Failed to archive fund",
    "admin.portfolio.fundArchived": "Fund archived",
    "admin.portfolio.fundUpdated": "Fund updated",
    "admin.portfolio.fundCreated": "Fund created",
    "admin.portfolio.errorSave": "Failed to save fund",

    // Admin - Charity Management page (GIV-256)
    "admin.charity.title": "Charity Management",
    "admin.charity.totalCount": "{{count}} total",
    "admin.charity.allStatuses": "All statuses",
    "admin.charity.searchPlaceholder": "Search by name\u2026",
    "admin.charity.colName": "Name",
    "admin.charity.colEin": "Tax ID",
    "admin.charity.taxIdDisplay": "Tax ID {{value}}",
    "admin.charity.colCategory": "Category",
    "admin.charity.colStatus": "Status",
    "admin.charity.colSigner": "Contact",
    "admin.charity.colJoined": "Joined",
    "admin.charity.colWallet": "Wallet",
    "admin.charity.colActions": "Actions",
    "admin.charity.walletNotSet": "Not set",
    "admin.charity.approve": "Approve",
    "admin.charity.reject": "Reject",
    "admin.charity.suspend": "Suspend",
    "admin.charity.reinstate": "Reinstate",
    "admin.charity.statusPending": "Pending",
    "admin.charity.statusVerified": "Verified",
    "admin.charity.statusApproved": "Approved",
    "admin.charity.statusRejected": "Rejected",
    "admin.charity.statusSuspended": "Suspended",
    "admin.charity.approveTitle": "Approve Charity",
    "admin.charity.rejectTitle": "Reject Charity",
    "admin.charity.suspendTitle": "Suspend Charity",
    "admin.charity.reinstateTitle": "Reinstate Charity",
    "admin.charity.confirmAction": "Confirm Action",
    "admin.charity.reasonRequired": "Reason (required)",
    "admin.charity.reasonOptional": "Reason (optional)",
    "admin.charity.saving": "Saving\u2026",
    "admin.charity.confirm": "Confirm",
    "admin.charity.noResults": "No charities found matching your filters.",
    "admin.charity.unnamed": "Unnamed organization",

    // Admin - Charity Requests page (GIV-256)
    "admin.charityRequests.title": "Charity Requests",
    "admin.charityRequests.subtitle":
      "Donor-submitted requests for unclaimed charities, grouped by organization. Highest-interest organizations should be prioritized for outreach.",
    "admin.charityRequests.ein": "Tax ID",
    "admin.charityRequests.requests": "Requests",
    "admin.charityRequests.latestRequester": "Latest Requester",
    "admin.charityRequests.latestRequest": "Latest Request",
    "admin.charityRequests.firstRequest": "First Request",
    "admin.charityRequests.empty":
      "No charity requests have been submitted yet.",
    "admin.charityRequests.errorTitle": "Error Loading Charity Requests",
    "admin.charityRequests.showing":
      "Showing {{shown}} of {{total}} unique organizations.",

    // Admin - Volunteer Validation page (GIV-256)
    "admin.validation.title": "Volunteer Validation Oversight",
    "admin.validation.totalCount": "{{count}} total requests",
    "admin.validation.pipelineStats": "Pipeline Statistics",
    "admin.validation.noStats": "No statistics available.",
    "admin.validation.pending": "Pending",
    "admin.validation.approved": "Approved",
    "admin.validation.rejected": "Rejected",
    "admin.validation.expired": "Expired",
    "admin.validation.avgResponseTime": "Avg response time:",
    "admin.validation.expirationRate": "Expiration rate:",
    "admin.validation.rejectionRate": "Rejection rate:",
    "admin.validation.searchPlaceholder": "Search volunteer, org\u2026",
    "admin.validation.requestsTab": "Validation Requests",
    "admin.validation.patternsTab": "Suspicious Patterns",
    "admin.validation.colVolunteer": "Volunteer",
    "admin.validation.colOrganisation": "Organisation",
    "admin.validation.colHours": "Hours",
    "admin.validation.colActivityDate": "Activity Date",
    "admin.validation.colHrsPerWeek": "Hrs/Week",
    "admin.validation.colTotalRequests": "Total Requests",
    "admin.validation.override": "Override",
    "admin.validation.noPatterns": "No suspicious patterns detected.",
    "admin.validation.noRequests": "No validation requests found.",
    "admin.validation.patternsDescription":
      "Volunteers flagged for reporting more than the configured threshold of hours in a rolling 7-day window. These patterns may indicate abuse of the self-reported hours system.",
    "admin.validation.overrideTitle": "Override Validation Request",
    "admin.validation.newStatus": "New Status",
    "admin.validation.reasonRequired": "Reason (required)",
    "admin.validation.reasonPlaceholder":
      "Explain the reason for overriding this request\u2026",
    "admin.validation.saving": "Saving\u2026",
    "admin.validation.confirmOverride": "Confirm Override",
    "admin.validation.allStatuses": "All statuses",
    "admin.validation.colStatus": "Status",
    "admin.validation.colCreated": "Created",

    // Home - Hero section (GIV-253 / GIV-263 fix)
    "home.hero.title": "Transparent Giving",
    "home.hero.titleAccent": "Sustainable Impact",
    "home.hero.description":
      "Radically transparent philanthropy for the digital age. Built for impact, backed by verifiable technology. Every contribution traceable from donor to destination.",
    "home.hero.visionTitle": "Our Vision",
    "home.hero.visionText":
      "Give Protocol is architecting the future of high-integrity giving. We leverage advanced technological infrastructure to bridge the gap between global capital and local impact. By integrating seamless digital-asset support with traditional giving methods, we are creating resilient funding ecosystems that transform one-time gifts into perpetual engines of support for the world\u2019s most vital causes.",
    "home.comingSoon": "Coming Soon",
    "home.readDocs": "Read Documentation",

    // Home - Features section
    "home.features.sectionTitle": "Your Gateway to Transparent Philanthropy",
    "home.features.sectionSubtitle":
      "Multichain infrastructure for seamless giving \u2014 crypto or fiat \u2014 with complete transparency",
    "home.features.highEfficiency.title": "High-Efficiency Giving",
    "home.features.highEfficiency.description":
      "Contribute directly to verified organizations with zero ambiguity. Our automated ledger provides real-time verification, ensuring your support is deployed exactly where it is needed most.",
    "home.features.equityFunds.title": "Charitable Equity Funds",
    "home.features.equityFunds.description":
      "Move beyond one-off donations by contributing to smart-managed equity funds. We utilize sophisticated, low-risk digital strategies to turn your principal into a perpetual stream of funding, sustaining charitable missions for generations to come.",
    "home.features.impactFunds.title": "Impact Funds",
    "home.features.impactFunds.description":
      "Pool resources for specific causes. Environmental, education, poverty relief - your donation amplified through collective action.",
    "home.features.verifiedOrgs.title": "Verified Organizations",
    "home.features.verifiedOrgs.description":
      "Browse any registered organization, or look for the verified badge. Organizations that opt in to our vetting process earn on-chain verification donors can trust.",
    "home.features.blockchainVerified.title": "Blockchain Verified",
    "home.features.blockchainVerified.description":
      "Soul-bound tokens recognize volunteer contributions. Skills, hours, and impact - all permanently recorded on-chain.",
    "home.features.bridgingModes.title": "Bridging Modes of Giving",
    "home.features.bridgingModes.description":
      "Whether you are giving in USD or digital assets, our multi-network architecture ensures your contribution moves at the speed of the modern world.",

    // Home - User roles section
    "home.roles.title": "Built for Everyone Changing the World",
    "home.roles.donors.title": "For Donors",
    "home.roles.donors.item1": "Give in crypto or fiat \u2014 your choice",
    "home.roles.donors.item2": "Governance participation",
    "home.roles.donors.item3": "On-chain reputation building",
    "home.roles.nonprofits.title": "For Non-Profit Organizations",
    "home.roles.nonprofits.item1": "Access new donor demographics",
    "home.roles.nonprofits.item2": "Sustainable funding via CEFs",
    "home.roles.nonprofits.item3": "Enhanced transparency reporting",
    "home.roles.volunteers.title": "For Volunteers",
    "home.roles.volunteers.item1": "Verifiable contribution records",
    "home.roles.volunteers.item2": "Portable skill credentials (SBTs)",
    "home.roles.volunteers.item3": "Achievement badges & recognition",

    // Home - Impact section
    "home.impact.title": "Planned Impact Funds",
    "home.impact.environmental.title": "Environmental Impact Fund",
    "home.impact.environmental.description":
      "Supporting reforestation, ocean cleanup, and renewable energy initiatives. Pooled donations directed to verified environmental organizations making measurable impact.",
    "home.impact.education.title": "Education Opportunity Fund",
    "home.impact.education.description":
      "Providing scholarships, digital learning tools, and teacher training. Collective funding amplified to support educational initiatives in underserved communities.",

    // Home - CTA section
    "home.cta.title": "Ready to Transform Giving?",
    "home.cta.subtitle":
      "Be among the first to experience transparent, blockchain-powered philanthropy. Join our community building a better future for charitable giving.",
    "home.cta.explore": "Explore Causes",

    // Home - Navigation
    "home.nav.features": "Features",
    "home.nav.impact": "Impact",
    "home.nav.charities": "Charities",
    "home.nav.volunteer": "Volunteer",

    // Home - Footer
    "home.footer.brand.tagline":
      "Transforming philanthropy through blockchain transparency and sustainable funding.",
    "home.footer.product.title": "Product",
    "home.footer.product.impactFunds": "Impact Funds",
    "home.footer.product.charities": "For Charities",
    "home.footer.product.volunteers": "For Volunteers",
    "home.footer.resources.title": "Resources",
    "home.footer.resources.whitepaper": "Whitepaper (Coming Soon)",
    "home.footer.resources.blog": "Blog (Coming Soon)",
    "home.footer.resources.community": "Community (Coming Soon)",
    "home.footer.connect.title": "Connect",
    "home.footer.connect.contact": "Contact (Coming Soon)",
    "home.footer.copyright":
      "Give Protocol. Multichain by design. Powered by Give Protocol volunteers.",

    // Auth - Sign in page (GIV-253 / GIV-263 fix)
    "auth.signin.welcomeBack": "Welcome back",
    "auth.signin.subtitle": "Sign in to your Give Protocol account",
    "auth.signin.emailPlaceholder": "Email",
    "auth.signin.passwordPlaceholder": "Password",
    "auth.signin.submitting": "Signing in\u2026",
    "auth.signin.withPasskey": "Sign in with Passkey",
    "auth.signin.withGoogle": "Continue with Google",
    "auth.signin.or": "or",
    "auth.signin.newToProtocol": "New to Give Protocol?",
    "auth.signin.createAccount": "Create an account \u2192",
    "auth.signin.forgotPasswordLink": "Forgot password? \u2192",
    "auth.signin.sslEncrypted": "TLS encrypted",
    "auth.signin.terms": "Terms",
    "auth.signin.privacy": "Privacy",
    "auth.signin.modeToggleLabel": "Sign-in method",

    // Auth - Left panel
    "auth.panel.headline": "Smart giving, transparent impact.",
    "auth.panel.subheadline":
      "One account. Donate by card or crypto. Track every dollar on-chain.",
    "auth.panel.statusLabel": "Protocol Status \u00b7 Genesis Phase",
    "auth.panel.statusDesc": "Building the foundation of transparent giving",
    "auth.panel.runsOn": "Runs on",

    // Auth - Toast notifications (GIV-302)
    "auth.toast.walletConnected": "Wallet connected",
    "auth.toast.signedInWith": "Signed in with {{address}}.",
    "auth.toast.welcomeBack": "Welcome back, {{firstName}}",
    "auth.toast.youreSignedIn": "You\u2019re signed in.",
    "auth.toast.signedOut": "Signed out",
    "auth.toast.signInFailed": "Sign-in failed",
    "auth.toast.signOutFailed": "Sign-out failed",

    // Auth - Wallet connection
    "auth.wallet.connect": "Connect Wallet",
    "auth.wallet.connecting": "Connecting wallet\u2026",
    "auth.wallet.signing": "Signing message\u2026",
    "auth.wallet.verifying": "Verifying\u2026",
    "auth.wallet.openingSession": "Opening session\u2026",

    // Auth - Validation
    "auth.validation.emailRequired": "Please enter your email address first",
    "auth.validation.invalidEmail": "Please enter a valid email address",
    "auth.validation.passwordTooShort":
      "Password must be at least 8 characters long",
    "auth.validation.passwordMismatch": "Passwords do not match",
    "auth.validation.ageAffirmationRequired":
      "Please confirm you are 16 years of age or older to continue.",

    // Login page (GIV-253 / GIV-263 fix)
    "auth.login.welcomeHeading": "Welcome to Give Protocol",
    "auth.login.signInOrConnect": "Sign In or Connect",
    "auth.login.skipToContent": "Skip to login content",
    "auth.login.donorTitle": "Donor Sign In",
    "auth.login.donorSubtitle": "Sign in to access your giving dashboard",
    "auth.login.charityTitle": "Nonprofit Portal",
    "auth.login.charitySubtitle":
      "Manage your organization profile and donations",
    "auth.login.back": "\u2190 Back",
    "auth.login.continueDonor": "Continue as Donor",
    "auth.login.connecting": "Connecting\u2026",
    "auth.login.connectWalletSignIn": "Connect Wallet & Sign In",
    "auth.login.newToPlatform": "New to Give Protocol?",
    "auth.login.newDonorSignUp": "New Donor Sign Up",
    "auth.login.manageNonprofit": "I manage a Nonprofit Profile",
    "auth.login.nonprofitTrayTitle": "New to Give Protocol?",
    "auth.login.createNonprofitAccount": "Create a Nonprofit Account",
    "auth.login.registerOrg": "Register your organization on Give Protocol",
    "auth.login.needHelp": "Need help?",
    "auth.login.forgotUsernameBtn": "Forgot username?",
    "auth.login.forgotPasswordBtn": "Forgot password?",

    // Donor login
    "auth.donorLogin.mismatch":
      "Donor User Account Not Found. This email is registered as a charity account. Please use the Charity Login.",
    "auth.donorLogin.signingIn": "Signing in\u2026",
    "auth.donorLogin.redirecting": "Redirecting in {{count}}...",

    // Donor registration
    "auth.donorReg.pleaseWait": "Please wait\u2026",
    "auth.donorReg.signUpPasskey": "Sign up with Passkey",
    "auth.donorReg.withGoogle": "Continue with Google",
    "auth.donorReg.connectWallet": "Connect Wallet",
    "auth.donorReg.orSetPassword": "Or set a password",
    "auth.donorReg.creating": "Creating Account...",
    "auth.donorReg.createAccount": "Create Donor Account",
    "auth.donorReg.privacyNotice": "By creating an account, you agree to our",
    "auth.donorReg.termsLink": "Terms of Service",
    "auth.donorReg.privacyAnd": "and acknowledge our",
    "auth.donorReg.privacyLink": "Privacy Policy",

    // Charity login
    "auth.charityLogin.mismatch":
      "This email is registered as a donor account. Please sign in at the donor portal.",
    "auth.charityLogin.signingIn": "Signing in...",
    "auth.charityLogin.or": "or",
    "auth.charityLogin.connecting": "Connecting...",
    "auth.charityLogin.connectWallet": "Connect Wallet",

    // Forgot credentials
    "auth.forgot.resetPassword": "Reset Password",
    "auth.forgot.forgotUsername": "Forgot Username",
    "auth.forgot.passwordDesc":
      "Enter your email address and we\u2019ll send you a link to reset your password.",
    "auth.forgot.usernameDesc":
      "Enter your email address and we\u2019ll send you your username.",
    "auth.forgot.passwordSuccess":
      "Password reset instructions sent to your email",
    "auth.forgot.usernameSuccess": "Username reminder sent to your email",
    "auth.forgot.checkEmail": "Check your email",
    "auth.forgot.backToSignIn": "Back to Sign In",
    "auth.forgot.emailPlaceholder": "Enter your email address",
    "auth.forgot.genericError": "An error occurred. Please try again.",
    "auth.forgot.sending": "Sending...",
    "auth.forgot.sendResetLink": "Send Reset Link",
    "auth.forgot.sendUsername": "Send Username",

    // Reset password (set new password page)
    "auth.resetPassword.title": "Set New Password",
    "auth.resetPassword.subtitle": "Choose a strong password for your account.",
    "auth.resetPassword.newPassword": "New Password",
    "auth.resetPassword.confirmPassword": "Confirm New Password",
    "auth.resetPassword.submit": "Update Password",
    "auth.resetPassword.updating": "Updating\u2026",
    "auth.resetPassword.mismatch": "Passwords do not match.",
    "auth.resetPassword.success": "Password updated successfully!",
    "auth.resetPassword.successSubtitle":
      "You can now sign in with your new password.",
    "auth.resetPassword.backToSignIn": "Back to Sign In",
    "auth.resetPassword.invalidLink":
      "This password reset link is invalid or has expired.",
    "auth.resetPassword.requestNew": "Request a new reset link",
    "auth.resetPassword.validating": "Validating reset link\u2026",

    // Organization search
    "auth.orgSearch.orgNameLabel": "Organization Name",
    "auth.orgSearch.searchPlaceholder": "Search by organization name...",
    "auth.orgSearch.countryLabel": "Country",
    "auth.orgSearch.allCountries": "All Countries",
    "auth.orgSearch.searchPrompt":
      "Search our database to find and claim your organization.",
    "auth.orgSearch.noResults": "No organizations found.",
    "auth.orgSearch.loadMore": "Load more results",
    "auth.orgSearch.cantFind": "Can\u2019t find your organization?",
    "auth.orgSearch.registerManually": "Register manually",

    // Charity claim form
    "charity.claim.orgDetails": "Organization Details (from Registry)",
    "charity.claim.orgNameLabel": "Name",
    "charity.claim.einLabel": "Tax ID",
    "charity.claim.locationLabel": "Location",
    "charity.claim.contactInfo": "Contact Information",
    "charity.claim.contactName": "Contact Name",
    "charity.claim.contactEmail": "Contact Email",
    "charity.claim.accountSecurity": "Account Security",
    "charity.claim.password": "Password",
    "charity.claim.confirmPassword": "Confirm Password",
    "charity.claim.backToSearch": "Back to search",
    "charity.claim.creating": "Creating Account...",
    "charity.claim.submit": "Claim Organization",
    "charity.claim.validation.name":
      "Name must be between 2 and 100 characters",
    "charity.claim.validation.email": "Please enter a valid email address",
    "charity.claim.validation.password":
      "Password must be at least 8 characters long",
    "charity.claim.validation.confirmPassword": "Passwords do not match",
    "charity.claim.validation.fix": "Please correct the validation errors",
    "charity.claim.useAsPublicEmail":
      "Use my email as the charity's public contact email",
    "charity.claim.publicContactEmail": "Organization public contact email",
    "charity.claim.privacyNotice":
      "Your personal signer email/phone is used for verification only and is not visible to other platform users. The public contact email is shown on your charity profile.",
    "charity.claim.secondContactTitle": "Second Contact",
    "charity.claim.secondContactName": "Second Contact Name",
    "charity.claim.secondContactEmail": "Second Contact Email",
    "charity.claim.secondContactHint":
      "A different person, notified when your charity's wallet or payout details change.",
    "charity.claim.validation.secondEmailDistinct":
      "Second contact email must be different from the primary email",
    "charity.claim.error.creation": "Account creation failed",
    "charity.claim.error.generic": "Failed to create account",

    // Charity vetting form
    "charity.vetting.orgDetails": "Organization Details",
    "charity.vetting.orgName": "Organization Name",
    "charity.vetting.description": "Description",
    "charity.vetting.taxId": "Tax or Registration ID",
    "charity.vetting.countryLabel": "Country",
    "charity.vetting.selectCountry": "Select Country",
    "charity.vetting.address": "Address",
    "charity.vetting.streetAddress": "Street Address",
    "charity.vetting.city": "City",
    "charity.vetting.state": "State/Province",
    "charity.vetting.postalCode": "Postal Code",
    "charity.vetting.contactInfo": "Contact Information",
    "charity.vetting.contactName": "Contact Name",
    "charity.vetting.contactEmail": "Contact Email",
    "charity.vetting.accountSecurity": "Account Security",
    "charity.vetting.password": "Password",
    "charity.vetting.confirmPassword": "Confirm Password",
    "charity.vetting.submitting": "Submitting Application...",
    "charity.vetting.submit": "Submit Charity Application",
    "charity.vetting.validation.orgName":
      "Organization name must be between 2 and 100 characters",
    "charity.vetting.validation.contactName":
      "Contact name must be between 2 and 100 characters",
    "charity.vetting.validation.email": "Please enter a valid email address",
    "charity.vetting.validation.password":
      "Password must be at least 8 characters long",
    "charity.vetting.validation.confirmPassword": "Passwords do not match",
    "charity.vetting.validation.description": "Description is required",
    "charity.vetting.validation.category": "Category is required",
    "charity.vetting.validation.taxId": "Tax ID is required",
    "charity.vetting.validation.streetAddress": "Street address is required",
    "charity.vetting.validation.city": "City is required",
    "charity.vetting.validation.country": "Country is required",
    "charity.vetting.validation.fix": "Please correct the validation errors",
    "charity.vetting.error.generic": "Failed to submit application",
    "charity.vetting.privacyNotice":
      "By submitting this application, you agree to our",
    "charity.vetting.termsLink": "Terms of Service",
    "charity.vetting.privacyAnd": "and acknowledge our",
    "charity.vetting.privacyLink": "Privacy Policy",

    // Common - shared auth fields
    "common.email": "Email",
    "common.password": "Password",
    "common.confirmPassword": "Confirm Password",
    "common.or": "or",

    // Footer component (GIV-253 / GIV-263 fix)
    "footer.brand.tagline":
      "Removing barriers to sustainable charitable giving.",
    "footer.resources.title": "Resources",
    "footer.resources.faq": "FAQ",
    "footer.resources.about": "About Us",
    "footer.legal.title": "Legal",
    "footer.legal.terms": "Terms of Service",
    "footer.legal.privacy": "Privacy Policy",
    "footer.legal.cookiePreferences": "Cookie preferences",
    "footer.connect.title": "Connect",
    "footer.copyright": "Give Protocol. All rights reserved.",

    // Cookie consent banner + customize modal (GIV-362)
    // GIV-353: revert Sentry transfer mechanism to "SCCs" once DPA is executed
    "consent.banner.ariaLabel": "Cookie consent",
    "consent.banner.body":
      "We use Google Analytics 4 (Google LLC, US) and Sentry (Functional Software, US/EU) to understand how the app is used and catch errors. No email, wallet address, or donor name is shared. You can withdraw consent at any time.",
    "consent.banner.privacyLink": "Privacy policy",
    "consent.banner.acceptAll": "Accept all",
    "consent.banner.decline": "Decline non-essential",
    "consent.banner.customize": "Customize",
    "consent.modal.title": "Cookie preferences",
    "consent.modal.essentialTitle": "Essential",
    "consent.modal.essentialDesc":
      "Required for core site features such as navigation and authentication. Cannot be disabled.",
    "consent.modal.essentialAlwaysOn": "Essential cookies — always on",
    "consent.modal.analyticsTitle":
      "Analytics (Google Analytics 4 · US, Sentry · US/EU)",
    "consent.modal.analyticsDesc":
      "Analytics cookies help us understand which pages are visited and how people navigate the app. We use Google Analytics 4 — session identifiers, page URLs, device/browser metadata, coarse location from anonymised IP (Google LLC, USA; SCCs + EU\u2013US Data Privacy Framework). Sentry — anonymised error traces and browser metadata for reliability monitoring (Functional Software, EU/USA; transfer mechanism pending). No email addresses, wallet addresses, or donor names are shared with either processor.",
    "consent.modal.analyticsOn": "Analytics & error replay — on",
    "consent.modal.analyticsOff": "Analytics & error replay — off",
    "consent.modal.cancel": "Cancel",
    "consent.modal.save": "Save preferences",

    // Navigation - additional (GIV-253 / GIV-263 fix)
    "nav.signIn": "Sign In",
    "nav.signOut": "Sign Out",
    "nav.privacy": "Privacy",

    // Auth - Signup page (GIV-264)
    "auth.signup.heading": "Create your account",
    "auth.signup.subtitle": "Join the transparent giving movement",
    "auth.signup.displayName": "Display name",
    "auth.signup.emailPlaceholder": "Email",
    "auth.signup.passwordPlaceholder": "Password",
    "auth.signup.confirmPasswordPlaceholder": "Confirm password",
    "auth.signup.submit": "Create Account",
    "auth.signup.submitting": "Creating account\u2026",
    "auth.signup.withPasskey": "Sign up with Passkey",
    "auth.signup.withGoogle": "Continue with Google",
    "auth.signup.connectWallet": "Connect Wallet",
    "auth.signup.orSetPassword": "Or set a password",
    "auth.signup.alreadyHaveAccount": "Already have an account?",
    "auth.signup.signInLink": "Sign in",
    "auth.signup.manageNonprofit": "Manage a nonprofit?",
    "auth.signup.trustText": "Your data is protected by",
    "auth.signup.and": "and",
    "auth.signup.privacyPolicy": "Privacy Policy",
    "auth.signup.panel.headline": "Giving that you can verify.",

    // Browse - Discovery (GIV-264)
    "browse.hero.title": "Transparent giving. Measurable change.",
    "browse.hero.subtitle":
      "Discover vetted causes, contribute via card or digital assets, and see the tangible footprint of your time and resources.",
    "browse.stats.networks": "Networks supported",
    "browse.stats.sectors": "Charitable sectors",
    "browse.stats.verifiedOrgs": "Verified organizations",
    "browse.stats.volunteerHours": "Volunteer hours",
    "browse.verified": "Verified",
    "browse.donate": "Donate",
    "browse.viewProfile": "View profile",
    "browse.filter.ariaLabel": "Filter charities",
    "browse.filter.searchAria": "Search charities",
    "browse.filter.locationAria": "Search location",
    "browse.results.ariaLabel": "Charity results",
    "browse.tabs.charities": "Charities",
    "browse.tabs.causes": "Causes",
    "browse.tabs.funds": "Portfolio Funds",
    "browse.tabs.ariaLabel": "Browse categories",
    "browse.featured.heading": "Featured organizations",
    "browse.featured.ariaLabel": "Featured organizations",
    "browse.featured.nextAria": "Next featured organizations",
    "browse.causes.heading": "Featured causes",
    "browse.causes.ariaLabel": "Featured causes",
    "browse.causes.badge": "Cause",
    "browse.causes.raised": "raised",
    "browse.causes.percentOf": "% of",
    "browse.causes.by": "by",
    "browse.causes.supportCta": "Support This Cause",
    "browse.causes.giveCta": "Give",
    "browse.causes.nextAria": "Next featured causes",
    "browse.funds.heading": "Portfolio Funds",
    "browse.funds.ariaLabel": "Portfolio Funds",
    "browse.funds.badge": "Portfolio Fund",
    "browse.funds.charity": "charity",
    "browse.funds.charities": "charities",
    "browse.funds.donateCta": "Donate to Fund",
    "browse.funds.nextAria": "Next portfolio funds",
    "portfolio.notFound.title": "Portfolio Fund Not Found",
    "portfolio.notFound.body":
      "The portfolio fund you are looking for does not exist or is no longer active.",
    "portfolio.supportedOrgs": "Supported Organizations",
    "browse.charity.einLabel": "Tax ID",
    "browse.charity.einDisplay": "Tax ID: {{value}}",
    "browse.charity.nteeLabel": "Sector Code",
    "browse.charity.deductibilityLabel": "Deductibility",
    "browse.charity.onPlatform": "On Platform",
    "browse.charity.loading": "Loading\u2026",
    "browse.charity.loadMore": "Load More",
    "browse.filter.searchPlaceholder": "Search charities...",
    "browse.filter.locationPlaceholder": "City, state, or country...",
    "browse.whyGiveProtocol.heading": "Why Give Protocol",
    "browse.whyGiveProtocol.verified.title": "Verified nonprofits",
    "browse.whyGiveProtocol.verified.description":
      "Every organization is matched against public registries before appearing on the platform.",
    "browse.whyGiveProtocol.onchain.title": "On-chain transparency",
    "browse.whyGiveProtocol.onchain.description":
      "Donations are recorded on the blockchain so you can trace your impact from wallet to cause.",
    "browse.whyGiveProtocol.volunteer.title": "Give time, not just money",
    "browse.whyGiveProtocol.volunteer.description":
      "Volunteer hours are verified by charities and count toward your protocol reputation.",
    "browse.news.heading": "Platform News",
    "admin.news.deleteConfirmBody": "“{{title}}” will be permanently removed. This can't be undone.",
    "admin.news.deleteConfirmTitle": "Delete news item?",
    "admin.news.saveFailed": "Couldn't save your changes. Please try again.",
    "admin.news.urlHelp": "Optional. Leave blank to show the item without a link.",
    "admin.news.urlInvalid": "Enter a full https:// address or a path starting with /.",
    "browse.carousel.error": "We couldn't load this section. Please refresh the page to try again.",
    "browse.carousel.goToPage": "Go to page {{page}}",
    "browse.carousel.pageLabel": "Page {{current}} of {{total}}",
    "browse.carousel.pause": "Pause automatic rotation",
    "browse.carousel.play": "Resume automatic rotation",
    "browse.causes.empty": "No causes available yet. Check back soon!",
    "browse.causes.prevAria": "Previous featured causes",
    "browse.causes.searchPlaceholder": "Search causes...",
    "browse.causes.subheading": "Support specific projects making real impact.",
    "browse.charity.registryIdDisplay": "Registry ID: {{value}}",
    "browse.featured.empty": "No featured organizations yet. Use the search above to find a charity.",
    "browse.featured.prevAria": "Previous featured organizations",
    "browse.featured.subheading": "A rotating look at verified charities on Give Protocol.",
    "browse.filter.addLocation": "Add location",
    "browse.filter.impactNotice": "Impact-location filtering isn't available yet, so results are not narrowed by impact location.",
    "browse.filter.locationHint": "Press Enter or choose Add to apply a location.",
    "browse.filter.minChars": "Type at least 2 characters to search.",
    "browse.funds.empty": "No portfolio funds available yet. Check back soon!",
    "browse.funds.prevAria": "Previous portfolio funds",
    "browse.funds.searchPlaceholder": "Search portfolio funds...",
    "browse.funds.subheading": "Curated giving portfolios that distribute donations across verified charities.",
    "browse.registry.badge": "{{registry}}-registered",
    "browse.results.empty": "No organizations match that search yet. Try a different keyword or add a location filter.",
    "browse.results.error": "We couldn't run that search. Please try again.",
    "browse.search.noMatches": "Nothing matches “{{query}}”.",
    "browse.category.A": "Arts & Culture",
    "browse.category.B": "Education",
    "browse.category.C": "Environment",
    "browse.category.D": "Animal Welfare",
    "browse.category.E": "Health",
    "browse.category.F": "Mental Health",
    "browse.category.G": "Medical Research",
    "browse.category.H": "Biomedical Research",
    "browse.category.I": "Crime & Legal",
    "browse.category.J": "Employment",
    "browse.category.K": "Food & Nutrition",
    "browse.category.L": "Housing",
    "browse.category.M": "Public Safety",
    "browse.category.N": "Recreation",
    "browse.category.O": "Youth Development",
    "browse.category.P": "Human Services",
    "browse.category.Q": "International",
    "browse.category.R": "Civil Rights",
    "browse.category.S": "Community Development",
    "browse.category.T": "Philanthropy",
    "browse.category.U": "Science & Technology",
    "browse.category.V": "Social Science",
    "browse.category.W": "Public Policy",
    "browse.category.X": "Religion",
    "browse.category.Y": "Mutual Benefit",
    "browse.category.default": "Nonprofit",

    // Causes (charity portal) (GIV-264)
    "cause.causes": "Causes",
    "cause.createNew": "Create New",
    "cause.noCausesTitle": "No causes yet",
    "cause.name": "Name",
    "cause.description": "Description",
    "cause.category": "Category",
    "cause.targetAmount": "Target Amount",
    "cause.headerImage": "Header Image",
    "cause.impact": "Impact",
    "cause.impactHelp": "Describe the expected impact of this cause.",
    "cause.location": "Location",
    "cause.timeline": "Timeline",
    "cause.partners": "Partners",
    "cause.limitReached": "Cause limit reached",

    // Impact Profile (GIV-264)
    "impact.statistics": "Impact Statistics",
    "impact.statLabel": "Label",
    "impact.statValue": "Value",
    "impact.missionStatement": "Mission Statement",
    "impact.missionPlaceholder": "Our mission is to...",
    "impact.highlights": "Impact Highlights",
    "impact.highlight": "Impact highlight",
    "impact.addHighlight": "Add Highlight",
    "impact.profile": "Impact Profile",
    "impact.loadError": "Failed to load impact profile",
    "impact.saveError": "Failed to save impact profile",
    "impact.saveSuccess": "Impact profile saved successfully",

    // Organization Profile (GIV-264)
    "organization.yearFounded": "Year Founded",
    "organization.address": "Address",
    "organization.street": "Street Address",
    "organization.city": "City",
    "organization.stateProvince": "State/Province",
    "organization.postalCode": "Postal Code",
    "organization.country": "Country",
    "organization.contactInfo": "Contact Information",
    "organization.phone": "Phone",
    "organization.email": "Email",
    "organization.website": "Website",
    "organization.socialMedia": "Social Media",
    "organization.publicFeed": "Public Feed",
    "organization.communityMedia": "Community & Media",
    "organization.profile": "Organization Profile",
    "organization.loadError": "Failed to load organization profile",
    "organization.saveError": "Failed to save organization profile",

    // Common - additional (GIV-264)
    "common.remove": "Remove",
    "common.saveChanges": "Save Changes",

    // Dashboard - additional stats (GIV-264)
    "dashboard.thisMonth": "This month",
    "dashboard.verified": "Verified volunteers",
    "dashboard.hoursLogged": "Hours logged",
    "dashboard.endorsements": "Endorsements",
    "dashboard.organizationsHelped": "Organizations Helped",
    "dashboard.yourOrganizationsHelped": "Your Organizations Helped",

    // Transactions (charity portal) (GIV-264)
    "transactions.createCampaign": "Create your first campaign",

    // Contributions - empty state (GIV-264)
    "contributions.emptyTitle": "Your giving journey starts here",
    "contributions.browseCharities": "Browse charities",

    // Charity Profile Page (GIV-264)
    "charity.profile.verified501c3": "Verified nonprofit",
    "charity.profile.verified501c3Tooltip":
      "Registered 501(c)(3) \u2014 donations may be tax-deductible in the United States",
    "charity.profile.statusClaimed": "Claimed",
    "charity.profile.statusUnclaimed":
      "Unclaimed \u2014 public registry data only",
    "charity.profile.statusIrsVerified": "IRS-verified nonprofit",
    "charity.profile.requestWidget.unclaimedBody":
      "This organization has not yet claimed their profile on Give Protocol. Donations become available once the organization claims its profile and completes wallet setup.",
    "charity.profile.rowEin": "Tax ID",
    "charity.profile.rowName": "Name",
    "charity.profile.rowLocation": "Location",
    "charity.profile.rowRulingYear": "Registration year",
    "charity.profile.rowNteeCode": "Sector code",
    "charity.profile.rowDeductibility": "Deductibility",
    "charity.profile.rowAffiliation": "Affiliation",
    "charity.profile.rowClassification": "Classification",
    "charity.profile.rowFoundation": "Foundation type",
    "charity.profile.rowActivityCodes": "Activity codes",
    "charity.profile.rowOrgType": "Organization type",
    "charity.profile.rowSubsection": "Subsection",
    "charity.profile.rowStatus": "Status",
    "charity.profile.registryRecord": "Registry Public Record",
    "charity.profile.einDisplay": "Tax ID",
    "charity.profile.charityNotFoundByTaxId":
      "We couldn't find a charity with this tax ID.",
    "charity.profile.registeredNonprofit": "Registered nonprofit",
    "charity.profile.registeredYear": "Registered {{year}}",
    "charity.profile.shareAria": "Share",
    "charity.profile.copied": "Copied!",
    "charity.profile.about": "About",
    "charity.profile.claimProfile": "Claim this profile",
    "charity.profile.noDescription": "No description available.",
    "charity.profile.toAddOne": "to add one.",
    "charity.profile.contact": "Contact",
    "charity.profile.donate.support": "Support",
    "charity.profile.donate.walletUnset":
      "This charity hasn't set up an official receiving wallet yet.",
    "charity.profile.donate.walletPending":
      "This charity is finishing wallet setup — donations are temporarily unavailable.",
    "charity.profile.donate.walletLegacy":
      "This charity is using a legacy wallet address.",
    "charity.profile.donate.feeNote":
      "0% platform fee on direct donations. Network gas fees apply.",
    "charity.profile.donate.jurisdictionUnavailable":
      "Donations to charities outside the United States are not yet available on Give Protocol.",

    // Wallet-type badge (GIV-290)
    "charity.walletBadge.safeLabel":
      "Multisig treasury · {{threshold}}-of-{{count}} signers",
    "charity.walletBadge.safeTooltip":
      "Donations go to a multi-signature wallet requiring multiple approvals before funds can move.",
    "charity.walletBadge.institutionalLabel": "Held at {{custodianName}}",
    "charity.walletBadge.institutionalTooltip":
      "Donations go to a wallet held by a qualified institutional custodian.",

    // Portfolio Funds page (GIV-264)
    "portfolio.title": "Portfolio Funds",

    // Settings - theme (GIV-264)
    "settings.displayPreferences": "Display Preferences",
    "settings.theme": "Theme",
    "settings.light": "Light",
    "settings.dark": "Dark",

    // Donation toast notifications (GIV-300)
    "donation.toast.received": "Donation received",
    "donation.toast.receivedMessage":
      "Thank you \u2014 your tax receipt is on the way.",
    "donation.toast.submitted": "Transaction submitted",
    "donation.toast.submittedMessage":
      "Waiting for on-chain confirmation\u2026",
    "donation.toast.confirmed": "Donation confirmed",
    "donation.toast.confirmedMessage":
      "Your contribution is recorded on-chain.",
    "donation.toast.failed": "Donation failed",

    // Art. 9(2)(a) donation consent (GIV-655)
    "donation.art9Consent.statement":
      "I understand my donation record links me to {{charity}}. Where that could indicate a religious, political, or philosophical affiliation, I explicitly consent to Give Protocol processing this record to complete and administer my donation.",
    "donation.art9Consent.label": "Donation data consent",
    "donation.art9Consent.required":
      "You must consent to donation data processing to proceed.",

    // Donation gas estimate (GIV-799)
    "donation.gas.estimating": "Estimating network fee…",
    "donation.gas.label": "Est. network fee",
    "donation.gas.highGasWarning":
      "High gas alert: estimated fee is {{usd}}, which is {{pct}} of your donation.",
    "donation.gas.highGasSuggestion":
      "Consider a larger donation or switching to a lower-fee chain.",
    "donation.gas.l2Hint": "For lower fees, try {{chains}}.",

    // Charity claim toast notifications (GIV-300)
    "charity.toast.verificationEmailSent": "Verification email sent",
    "charity.toast.verificationEmailMessage":
      "Check your inbox \u2014 the link expires in 24 hours.",
    "charity.toast.submissionFailed": "Submission failed",

    // Admin Audit Trail (GIV-415)
    "admin.auditTrail.filterByAction": "Filter by action",
    "admin.auditTrail.allActions": "All actions",
    "admin.auditTrail.action.charityStatusChange": "Charity status change",
    "admin.auditTrail.action.userStatusChange": "User status change",
    "admin.auditTrail.action.donationFlag": "Donation flag",
    "admin.auditTrail.action.donationFlagResolve": "Donation flag resolve",
    "admin.auditTrail.action.validationOverride": "Validation override",
    "admin.auditTrail.action.configChange": "Config change",
    "admin.auditTrail.action.verificationApprove": "Verification approve",
    "admin.auditTrail.action.verificationReject": "Verification reject",
    "admin.auditTrail.action.charitySuspend": "Charity suspend",
    "admin.auditTrail.action.charityReinstate": "Charity reinstate",
    "admin.auditTrail.action.userSuspend": "User suspend",
    "admin.auditTrail.action.userReinstate": "User reinstate",
    "admin.auditTrail.action.userBan": "User ban",
    "admin.auditTrail.action.viewPii": "View PII",
    "admin.auditTrail.action.viewPiiList": "View PII list",
    "admin.auditTrail.viewedEntity":
      "Admin {{adminId}} viewed {{entityType}} {{entityId}}",
    "admin.auditTrail.viewedList":
      "Admin {{adminId}} viewed {{entityType}} list (page {{page}}, filters: {{filters}})",
    "admin.auditTrail.viewedListNoFilters":
      "Admin {{adminId}} viewed {{entityType}} list (page {{page}})",

    // Privacy Policy §6 — Admin access audit log (GIV-416)
    "privacy.security.auditLog":
      "As a technical measure under GDPR Article 32, we log and monitor administrative access to personal data \u2014 including the action taken and when it occurred \u2014 to detect unauthorized access, demonstrate accountability, and reconstruct events in the unlikely event of a personal data breach (Article 34). Donor personal data is not duplicated into these administrative-access logs. Administrative-access logs are distinct from the authentication and security logs described in \u00a77.1; their retention period is governed by our internal Record of Processing Activities.",

    // Team page
    "team.title": "Our Team",
    "team.subtitle":
      "Give Protocol is built by a dedicated team of engineers, nonprofit leaders, and blockchain experts working to bring transparency and efficiency to charitable giving.",
    "team.comingSoon": "Team Profiles Coming Soon",
    "team.comingSoonDesc":
      "We are updating our team directory. Check back shortly to meet the people behind Give Protocol.",

    // CFIA acceptance flow (GIV-1051)
    "cfia.signaturePad.ariaLabel": "Signature drawing area",
    "cfia.signaturePad.placeholder": "Sign here",
    "cfia.signaturePad.clear": "Clear signature",
    "cfia.signaturePad.clearButton": "Clear",
    "cfia.modal.title": "Charitable Fundraising Industry Agreement",
    "cfia.modal.loading": "Loading agreement status\u2026",
    "cfia.modal.reviewIntro":
      "Please read the agreement below in full. You must scroll to the end before proceeding.",
    "cfia.modal.version": "Version: {{version}}",
    "cfia.modal.proceedToSign":
      "I have read the agreement \u2014 proceed to sign",
    "cfia.modal.scrollToEnd": "Scroll to the end to continue",
    "cfia.modal.signingIntro":
      "Confirm your identity as the authorized signer and provide your signature below.",
    "cfia.modal.signerLegend": "Authorized signer details",
    "cfia.modal.signerName": "Full name",
    "cfia.modal.signerNamePlaceholder": "Jane Smith",
    "cfia.modal.signerTitle": "Title / Role",
    "cfia.modal.signerTitlePlaceholder": "Executive Director",
    "cfia.modal.signerEmail": "Email",
    "cfia.modal.signerEmailPlaceholder": "jane@example.org",
    "cfia.modal.signatureLabel": "Signature",
    "cfia.modal.back": "Back",
    "cfia.modal.submit": "Accept and Sign",
    "cfia.modal.submitting": "Recording your acceptance\u2026",
    "cfia.modal.acceptedTitle": "Agreement Accepted",
    "cfia.modal.acceptedBody":
      "The Charitable Fundraising Industry Agreement has been accepted by {{name}} on {{date}}.",
    "cfia.modal.close": "Close",
    "cfia.modal.errorTitle": "Something went wrong",
    "cfia.modal.errorDefault": "An error occurred. Please try again.",
    "cfia.modal.retry": "Try Again",
    "cfia.legalText.title": "Charitable Fundraising Industry Agreement",
    "cfia.legalText.draftNotice":
      "This is a placeholder. The final legal text has not yet been delivered. This agreement cannot be activated until the legal text is finalized.",
    "cfia.legalText.placeholder1":
      'This Charitable Fundraising Industry Agreement (the "Agreement") is entered into between Give Protocol Foundation ("Platform") and the undersigned charitable organization ("Charity").',
    "cfia.legalText.placeholder2":
      "By signing below, the Charity acknowledges and agrees to the terms and conditions governing the use of the Give Protocol platform for the purpose of receiving charitable donations.",
    "cfia.legalText.placeholder3":
      "[Full legal text will be inserted here upon finalization by legal counsel. Sections will cover: platform obligations, charity obligations, fee structure, data handling, dispute resolution, termination, and governing law.]",
    "cfia.legalText.endMarker": "--- End of agreement text ---",
    "cfia.onboarding.acceptCfia": "Accept CFIA",
  },
};
